import React from 'react';
import PropTypes from 'prop-types';

import withMessages from 'component-utilities/intl';

import FormContainer from 'v-c/Forms/FormContainer';
import FormContainerTitle from 'v-c/Forms/FormContainerTitle';
import FormRow from 'v-c/Forms/FormRow';
import FormItemLarge from 'v-c/Forms/FormItemLarge';
import FormLabel from 'v-c/Forms/FormLabel';
import FormText from 'v-c/Forms/FormText';
import FormField from 'v-c/Forms/FormField';
import FormButtons from 'v-c/Forms/FormButtons';
import FormErrorText from 'v-c/Forms/FormErrorText';
import InputField from 'v-c/Forms/InputFields/InputFieldV2';

import PrimaryButton from 'v-c/Buttons/PrimaryButton';

import PageContent from '../Components/PageContent';
import ContentHeader from '../Components/ContentHeader/ContentHeader';
import ContentTitle from '../Components/ContentHeader/ContentTitle';
import ContentBody from '../Components/ContentBody';

import { stateLogic, viewLogic } from './ChangePasswordDeconstructed';

export const BASE_KEY = 'components.templates.change_password';
export const VALIDATION_KEY = 'components.validation_issues';
export const VALIDATION_ISSUES = {
    USERNAME_PASSWORD_MATCH: `${VALIDATION_KEY}.username_password_match`,
    DOES_NOT_MATCH: `${VALIDATION_KEY}.does_not_match`,
    PASSWORD_COMPLEXITY: `${VALIDATION_KEY}.password_complexity`,
};

function ChangePasswordComponent(props) {
    const {
        formError,
        inputStatuses,
        isButtonDisabled,
        user,
        getMessage,
        passwordConfig,
        confirmPasswordConfig,
        onSave,
    } = viewLogic(props);

    function renderFormError() {
        return (
            <FormErrorText
                field="password-error"
                attributes={{
                    'data-error-type': formError,
                }}
                error={formError}
                values={{
                    field: 'NEW PASSWORD',
                    matchField: 'CONFIRM PASSWORD',
                }}
            />
        );
    }

    return (
        <PageContent>
            <ContentHeader>
                <ContentTitle text={`${user.fullName} `} counter={{ index: user.username }} />
            </ContentHeader>
            <ContentBody>
                <FormContainer>
                    <FormContainerTitle>
                        {getMessage(`${BASE_KEY}.change_password`)}
                    </FormContainerTitle>
                    <FormRow>
                        <FormItemLarge>
                            <FormLabel>
                                {getMessage(`${BASE_KEY}.new_password`)}
                            </FormLabel>
                            <FormField>
                                <InputField
                                    focused
                                    data-field="new-password"
                                    type="password"
                                    status={inputStatuses}
                                    {...passwordConfig}
                                />
                            </FormField>
                            {formError && formError !== VALIDATION_ISSUES.DOES_NOT_MATCH && renderFormError()}
                        </FormItemLarge>
                        <FormItemLarge>
                            <FormLabel>
                                {getMessage(`${BASE_KEY}.confirm_password`)}
                            </FormLabel>
                            <FormField>
                                <InputField
                                    data-field="confirm-password"
                                    type="password"
                                    status={inputStatuses}
                                    {...confirmPasswordConfig}
                                />
                            </FormField>
                            {formError && formError === VALIDATION_ISSUES.DOES_NOT_MATCH && renderFormError()}
                        </FormItemLarge>
                        <FormItemLarge>
                            <FormField>
                                <FormText>
                                    {getMessage(`${BASE_KEY}.password_hint`)}
                                </FormText>
                            </FormField>
                        </FormItemLarge>
                    </FormRow>
                    <FormRow>
                        <FormButtons>
                            <PrimaryButton
                                data-action="change-password"
                                onClick={onSave}
                                disabled={isButtonDisabled}
                            >
                                {getMessage(`${BASE_KEY}.save`)}
                            </PrimaryButton>
                        </FormButtons>
                    </FormRow>
                </FormContainer>
            </ContentBody>
        </PageContent>
    );
}

ChangePasswordComponent.propTypes = {
    getMessage: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    user: PropTypes.shape({
        fullName: PropTypes.string.isRequired,
        username: PropTypes.string.isRequired,
    }).isRequired,
    passwordConfig: PropTypes.shape({
        onChange: PropTypes.func.isRequired,
        value: PropTypes.string.isRequired,
    }).isRequired,
    confirmPasswordConfig: PropTypes.shape({
        onChange: PropTypes.func.isRequired,
        value: PropTypes.string.isRequired,
    }).isRequired,
    isButtonDisabled: PropTypes.bool,
};

ChangePasswordComponent.defaultProps = {
    isButtonDisabled: false,
};

const ChangePassword = withMessages(stateLogic(VALIDATION_ISSUES)(ChangePasswordComponent));

export default ChangePassword;
